import React, {
    FormEvent,
    ReactElement,
    useEffect,
    useRef,
    useState,
} from "react";
import styles from "./styles.module.scss";
import { useInsurance } from "@hooks/useInsuranceContext";
import ButtonArrow from "../buttonArrow";
import { useRouter } from "next/router";
import { gettingQueriesAsString } from "@lib/shared/routerQueriesToString";
import { useDomainContext } from "@hooks/useDomainContext";
import ListingFilters from "@components/shared/listingFilters";
import { arrayToKeyedObject, base64EncodeUnicode } from "@lib/sharedUtils";
import { questions } from "@components/shared/listingFilters/questions";
import { useModal } from "@hooks/useModalContext";

export default function SellingBox(): ReactElement {
    const { insuranceState, setInsuranceCategory } = useInsurance();
    const { initiateQuestions, setQuestionValue, modal } = useModal();
    const { domain, category: sellingBoxCategory } = useDomainContext();
    const categories = domain?.categories;
    const [error, setError] = useState(false);

    const hiddenInputRef = useRef<HTMLInputElement | null>(null);
    const router = useRouter();

    const { insuranceCategory } = insuranceState.sellingBox;

    const { questions: filters } = modal.listing;

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const gettingFilters = () => {
            const finalFilters: { [x: string]: string } = {};
            Object.keys(filters).forEach((filterKey) => {
                const filter = filters[filterKey] as
                    | { value?: string; name?: string }
                    | undefined;
                const value = filter?.value;
                const name = filter?.name;

                if (!value || !name) {
                    return;
                }

                finalFilters[name] = value;
            });

            return base64EncodeUnicode(JSON.stringify(finalFilters));
        };

        const zipCodeFilter = filters["zipCode"] as
            | { value?: string }
            | undefined;
        const zipCodeValue = zipCodeFilter?.value ?? "";

        if (zipCodeValue.length >= 4) {
            hiddenInputRef.current?.focus();
            const formattedZipCode =
                zipCodeValue.length === 4 ? `0${zipCodeValue}` : zipCodeValue;

            window.location.href = `${window.location.origin}/${
                sellingBoxCategory?.slugAlias ??
                (insuranceCategory?.slugAlias as string)
            }/offers?zc=${formattedZipCode}&filters=${gettingFilters()}&referrer=${
                sellingBoxCategory?.slugAlias ?? "home"
            }&${gettingQueriesAsString(router.query, "inline", ["category"])}`;
        } else {
            setError(true);
        }
    };

    const checkZipCode = () => {
        if (localStorage.getItem("filters")) {
            setQuestionValue({
                name: "zipCode",
                value:
                    (
                        JSON.parse(
                            localStorage.getItem("filters") as string,
                        ) as {
                            [x: string]: string;
                        }
                    ).zipCode ?? "",
            });
        }
        setTimeout(() => {
            document.getElementsByName("zipCode")[0]?.focus();
        }, 100);
    };

    useEffect(() => {
        const categorySlug = sellingBoxCategory
            ? sellingBoxCategory.slugAlias
            : insuranceCategory?.slugAlias;

        if (categorySlug && questions[categorySlug]) {
            initiateQuestions(
                arrayToKeyedObject(
                    questions[categorySlug].filter(
                        (question) => question.isInHomePage,
                    ) as any[],
                    "name",
                ),
            );
        }
        if (insuranceCategory) checkZipCode();
    }, [insuranceCategory]);

    useEffect(() => {
        checkZipCode();
    }, []);

    return (
        <>
            <div className={styles["selling-box"]}>
                {!sellingBoxCategory && (
                    <div className={styles["insurance-type"]}>
                        <div className={styles["insurance-radio"]}>
                            {categories?.map((categoryItem, index) => (
                                <div
                                    className={styles["radio-item"]}
                                    key={index}
                                >
                                    <input
                                        id={categoryItem.slug}
                                        type="radio"
                                        value={categoryItem.slug}
                                        className="hidden"
                                        name="insurance-category"
                                        onChange={(e) => {
                                            setInsuranceCategory(
                                                categories.find(
                                                    (catItem) =>
                                                        catItem.slug ===
                                                        e.target.value,
                                                ) ?? null,
                                            );
                                        }}
                                        checked={
                                            insuranceCategory?.slug ===
                                            categoryItem?.slug
                                        }
                                    />
                                    <label htmlFor={categoryItem.slug}>
                                        <span className={styles["icon"]}>
                                            <img
                                                src={
                                                    categoryItem?.icon?.fullPath
                                                }
                                                alt={`${categoryItem.name} icon`}
                                            />
                                        </span>
                                        <span className={styles["title"]}>
                                            {categoryItem.slugAlias}
                                        </span>
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <form className={styles["form"]} onSubmit={handleSubmit}>
                    <ListingFilters
                        className={styles["filters"]}
                        selectQuestionClassName={styles["select-question"]}
                        toggleQuestionClassName={styles["toggle-question"]}
                        textQuestionClassName={styles["text-question"]}
                        rowClassName={styles["row"]}
                        isQuestionSameRow={true}
                        noMinimize
                    />
                    <div className={styles["button"]}>
                        <button type="submit" id="viewRates">
                            View{" "}
                            {sellingBoxCategory?.slugAlias === "medicare"
                                ? "Plans"
                                : "Rates"}
                            <ButtonArrow />
                        </button>
                    </div>
                    {error && (
                        <span className={styles["error"]}>
                            Please enter a valid zip code
                        </span>
                    )}
                </form>
            </div>
            <div>
                <input
                    type="number"
                    ref={hiddenInputRef}
                    className="select-none opacity-0 absolute -top-48 pointer-events-none"
                    autoComplete="off"
                />
            </div>
        </>
    );
}
